import React from 'react';
import CategoryIcon from '../common/CategoryIcon';
import Card from '../common/Card';

const SpendPrediction = ({ balance, plannedTransactions, predictions, monthName, lastMonthName, balanceLabel }) => {
  const totalPlanned = plannedTransactions.reduce((sum, t) => sum + t.amount, 0);
  const totalPredicted = predictions.reduce((sum, p) => sum + p.predicted_amount, 0);
  const result = balance + totalPlanned - totalPredicted;

  const sortedPlanned = [...plannedTransactions].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const sortedPredictions = [...predictions].sort((a, b) => b.predicted_amount - a.predicted_amount);

  return (
    <Card className="p-6 shadow-sm flex flex-col h-full">
      <h3 className="text-neutral-400 font-bold text-sm uppercase tracking-wider mb-6">
        Vorhersage {monthName}
      </h3>
      
      <div className="space-y-5 flex-1">
        <div className="flex justify-between items-center pb-3 border-b border-neutral-100">
          <span className="text-sm font-medium text-neutral-500">{balanceLabel || `Übertrag aus ${lastMonthName}`}</span>
          <span className="text-sm font-bold text-neutral-800">
            {balance.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </span>
        </div>

        {/* Planned */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm font-bold text-neutral-400 uppercase tracking-wider">
            <span>Geplante Einträge</span>
            <span className={`font-semibold ${totalPlanned >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {totalPlanned.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </span>
          </div>
          {sortedPlanned.length > 0 ? (
            <div className="space-y-1.5 pl-2 overflow-y-auto pr-1">
              {sortedPlanned.map((t, i) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span className="text-neutral-600 truncate mr-2 font-medium">{t.name}</span>
                  <span className={`font-bold ${t.amount >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {t.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-neutral-400 pl-2 italic">Keine geplanten Einträge</div>
          )}
        </div>

        {/* Predicted */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm font-bold text-neutral-400 uppercase tracking-wider">
            <span>Prognostizierte Ausgaben</span>
            <span className="text-red-500 font-semibold">
              {(-totalPredicted).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </span>
          </div>
          {sortedPredictions.length > 0 ? (
            <div className="space-y-1.5 pl-2 overflow-y-auto pr-1">
              {sortedPredictions.map((p, i) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-1.5 truncate mr-2 text-neutral-600 font-medium">
                    <CategoryIcon name={p.icon_name} size={13} className="text-neutral-400 flex-shrink-0" />
                    <span className="truncate">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {p.original_amount !== undefined && p.original_amount !== p.predicted_amount && (
                        <span className="text-[11px] text-neutral-400 line-through">
                        {p.original_amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </span>
                    )}
                    <span className="text-red-500 font-bold">
                    {(-p.predicted_amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-neutral-400 pl-2 italic">Keine Vorhersagen konfiguriert</div>
          )}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-neutral-100">
        <div className="flex justify-between items-center">
          <span className="text-sm font-bold text-neutral-700">Erwarteter Kontostand</span>
          <span className={`text-md font-bold ${result >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {result.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </span>
        </div>
        <p className="text-[11px] text-neutral-400 mt-2 text-center italic leading-tight">
          Die Schätzung ist meist konservativ und kann von der Realität abweichen.
        </p>
      </div>
    </Card>
  );
};

export default SpendPrediction;
